/**
 * Floating onboarding checklist component.
 *
 * Displays a floating widget that guides farmers through farm setup.
 * Can be expanded/collapsed and dismissed when complete.
 */

import CelebrationIcon from "@mui/icons-material/Celebration";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Fab,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  Link as MuiLink,
  Paper,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import confetti from "canvas-confetti";
import { AnalyticsEvents, mixpanel } from "common/analytics";
import { useTranslation } from "common/locales";
import {
  getAllArableFieldGeomsFromFields,
  getAllFieldGeomsFromFields,
  useArableFields,
  useCurrentFarmAnimalGroups,
  useFields,
} from "model";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import YouTube from "react-youtube";
import { useUIStore } from "stores";
import { ONBOARDING_STEPS, OnboardingStep } from "./onboardingSteps";
import { useOnboardingState } from "./useOnboardingState";

/**
 * Routes where the onboarding checklist should be hidden on mobile.
 * These are typically routes where the user is actively completing onboarding steps.
 */
const HIDE_ON_MOBILE_ROUTES = [
  "/add-farm",
  "/add-field",
  "/add-animal-group",
  "/animal-group/", // When viewing animal group (for assign field)
];

/**
 * Main onboarding checklist component.
 * Renders as a floating widget in the bottom-right corner.
 */
export function OnboardingChecklist() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    isComplete,
    isDismissed,
    totalSteps,
    completedSteps,
    steps,
    isLoading,
    dismiss,
  } = useOnboardingState();

  // Get animal groups for dynamic route resolution
  const { data: animalGroups = [] } = useCurrentFarmAnimalGroups();

  // Get fields to determine if we should show SBI import modal
  const { data: fields = [] } = useFields();
  const { data: arableFields = [] } = useArableFields();
  const fieldGeoms = useMemo(
    () => getAllFieldGeomsFromFields(fields),
    [fields]
  );
  const arableFieldGeoms = useMemo(
    () => getAllArableFieldGeomsFromFields(arableFields),
    [arableFields]
  );
  const totalFieldCount =
    fieldGeoms.features.length + arableFieldGeoms.features.length;

  // Get SBI modal control from UI store
  const openSbiImportModal = useUIStore((state) => state.openSbiImportModal);

  // Get onboarding completion dialog state from UI store
  // This is triggered by AssignFieldDialog when the first field is assigned
  const showCompletionDialog = useUIStore(
    (state) => state.onboardingCompletionDialogOpen
  );
  const closeCompletionDialog = useUIStore(
    (state) => state.closeOnboardingCompletionDialog
  );

  // On mobile, hide checklist when user is on an onboarding-related route
  const shouldHideOnMobile =
    isMobile &&
    HIDE_ON_MOBILE_ROUTES.some((route) => location.pathname.startsWith(route));

  const [isExpanded, setIsExpanded] = useState(true);
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<{
    id: string;
    title: string;
  } | null>(null);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleAction = useCallback(
    (step: OnboardingStep) => {
      mixpanel.track(AnalyticsEvents.ONBOARDING_STEP_CLICKED, {
        step: step.id,
      });
      // Collapse checklist on mobile after any action to avoid blocking UI
      if (isMobile) {
        setIsExpanded(false);
      }

      // For "add-field" step, show SBI import modal when there are < 5 fields
      if (step.id === "add-field" && totalFieldCount < 5) {
        openSbiImportModal();
        return;
      }

      let route = step.actionRoute;
      // Replace dynamic :animalGroupId with actual first animal group ID
      if (route.includes(":animalGroupId") && animalGroups.length > 0) {
        route = route.replace(":animalGroupId", animalGroups[0].guid);
      }
      navigate(route);
    },
    [navigate, animalGroups, totalFieldCount, openSbiImportModal, isMobile]
  );

  const handleWatchVideo = useCallback(
    (step: OnboardingStep) => {
      mixpanel.track(AnalyticsEvents.ONBOARDING_VIDEO_PLAYED, {
        step: step.id,
      });
      // Collapse checklist on mobile after opening video to avoid blocking UI
      if (isMobile) {
        setIsExpanded(false);
      }

      const videoId = isMobile ? step.mobileVideoId : step.videoId;
      if (videoId) {
        setSelectedVideo({
          id: videoId,
          title: t(step.titleKey),
        });
        setVideoDialogOpen(true);
      }
    },
    [isMobile, t]
  );

  const closeVideoDialog = useCallback(() => {
    setVideoDialogOpen(false);
    setSelectedVideo(null);
  }, []);

  // Don't show checklist if dismissed, complete, loading, or hidden on mobile
  // But still render completion dialog if it's open
  if (isDismissed || isComplete || isLoading || shouldHideOnMobile) {
    return (
      <CompletionDialog
        open={showCompletionDialog}
        onClose={closeCompletionDialog}
      />
    );
  }

  const progress = (completedSteps / totalSteps) * 100;

  return (
    <>
      <FloatingContainer isMobile={isMobile}>
        <Paper
          elevation={3}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              px: 2,
              py: 1.5,
              borderBottom: 1,
              borderColor: "divider",
              cursor: "pointer",
              bgcolor: "background.paper",
              "&:hover": { bgcolor: "action.hover" },
            }}
            onClick={toggleExpanded}
          >
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {t("onboarding.checklist.title")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t("onboarding.checklist.progress", {
                  completed: completedSteps,
                  total: totalSteps,
                })}
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                mixpanel.track(AnalyticsEvents.ONBOARDING_DISMISSED);
                dismiss();
              }}
              aria-label={t("onboarding.checklist.dismiss")}
              sx={{ mr: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" aria-label="toggle">
              {isExpanded ? <ExpandMoreIcon /> : <ExpandLessIcon />}
            </IconButton>
          </Box>

          {/* Progress bar */}
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{ height: 3 }}
          />

          {/* Checklist items */}
          <Collapse in={isExpanded}>
            <List
              disablePadding
              sx={{
                bgcolor: "background.paper",
                maxHeight: isMobile
                  ? "calc(100vh - 240px)"
                  : "calc(100vh - 200px)",
                overflow: "auto",
              }}
            >
              {ONBOARDING_STEPS.map((step) => {
                const stepState = steps.find((s) => s.id === step.id);
                const isCompleted = stepState?.completed ?? false;
                const isActionable = stepState?.actionable ?? false;

                return (
                  <ChecklistItem
                    key={step.id}
                    step={step}
                    isCompleted={isCompleted}
                    isActionable={isActionable}
                    onAction={() => handleAction(step)}
                    onWatchVideo={
                      step.videoId ? () => handleWatchVideo(step) : undefined
                    }
                  />
                );
              })}
            </List>
          </Collapse>
        </Paper>
      </FloatingContainer>

      {/* Video Dialog */}
      <VideoDialog
        open={videoDialogOpen}
        onClose={closeVideoDialog}
        videoId={selectedVideo?.id}
        title={selectedVideo?.title}
      />
    </>
  );
}

// ============ SUBCOMPONENTS ============

interface ChecklistItemProps {
  step: OnboardingStep;
  isCompleted: boolean;
  isActionable: boolean;
  onAction: () => void;
  onWatchVideo?: () => void;
}

function ChecklistItem({
  step,
  isCompleted,
  isActionable,
  onAction,
  onWatchVideo,
}: ChecklistItemProps) {
  const { t } = useTranslation();

  return (
    <ListItem
      sx={{
        flexDirection: "column",
        alignItems: "stretch",
        py: 1,
        px: 2,
        opacity: isCompleted ? 0.7 : isActionable ? 1 : 0.5,
        bgcolor: isActionable ? "rgba(56, 142, 60, 0.08)" : "background.paper",
        position: "relative",
        "&::before": isActionable
          ? {
              content: '""',
              position: "absolute",
              left: 0,
              top: 0,
              bottom: 0,
              width: 3,
              bgcolor: "primary.main",
            }
          : undefined,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
        <ListItemIcon sx={{ minWidth: 28, mt: 0.25 }}>
          {isCompleted ? (
            <CheckCircleIcon color="success" fontSize="small" />
          ) : (
            <CheckCircleOutlineIcon
              color={isActionable ? "primary" : "disabled"}
              fontSize="small"
            />
          )}
        </ListItemIcon>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {/* Title row with action button on right for actionable steps */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Typography
              variant="body2"
              fontWeight={isActionable ? 600 : 400}
              color={isCompleted ? "text.secondary" : "text.primary"}
              sx={{
                textDecoration: isCompleted ? "line-through" : "none",
              }}
            >
              {t(step.titleKey)}
            </Typography>
            {isActionable && (
              <Button
                size="small"
                variant="contained"
                onClick={onAction}
                sx={{ textTransform: "none", flexShrink: 0 }}
              >
                {t(step.actionKey)}
              </Button>
            )}
          </Box>
          {/* Description for incomplete, non-actionable steps */}
          {!isCompleted && !isActionable && (
            <Typography variant="caption" color="text.secondary">
              {t(step.descriptionKey)}
            </Typography>
          )}
          {/* Video link - shown for steps with videos (actionable or completed) */}
          {onWatchVideo && (isActionable || isCompleted) && (
            <Typography
              component="span"
              variant="caption"
              onClick={onWatchVideo}
              sx={{
                color: "primary.main",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 0.5,
                mt: 0.25,
                "&:hover": { textDecoration: "underline" },
              }}
            >
              <PlayCircleOutlineIcon sx={{ fontSize: 14 }} />
              {t("onboarding.checklist.watchVideo")}
            </Typography>
          )}
        </Box>
      </Box>
    </ListItem>
  );
}

interface VideoDialogProps {
  open: boolean;
  onClose: () => void;
  videoId?: string;
  title?: string;
}

function VideoDialog({ open, onClose, videoId, title }: VideoDialogProps) {
  const { t } = useTranslation();

  const playerVars = {
    autoplay: 1,
    modestbranding: 1,
    rel: 0,
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h6">{title}</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, aspectRatio: "16/9" }}>
        {videoId && (
          <YouTube
            videoId={videoId}
            opts={{
              width: "100%",
              height: "100%",
              playerVars,
            }}
            style={{ display: "block", height: "100%" }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

interface CompletionDialogProps {
  open: boolean;
  onClose: () => void;
}

function CompletionDialog({ open, onClose }: CompletionDialogProps) {
  const { t } = useTranslation();

  // Trigger confetti when dialog opens
  useEffect(() => {
    if (open) {
      mixpanel.track(AnalyticsEvents.ONBOARDING_COMPLETED);
      // Fire confetti from both sides
      const fireConfetti = () => {
        void confetti({
          particleCount: 100,
          spread: 70,
          origin: { x: 0.1, y: 0.6 },
        });
        void confetti({
          particleCount: 100,
          spread: 70,
          origin: { x: 0.9, y: 0.6 },
        });
      };
      // Small delay so the dialog is visible first
      const timer = setTimeout(fireConfetti, 200);
      return () => clearTimeout(timer);
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogContent sx={{ textAlign: "center", py: 4 }}>
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: "50%",
            bgcolor: "success.light",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mx: "auto",
            mb: 3,
          }}
        >
          <CelebrationIcon sx={{ fontSize: 40, color: "success.dark" }} />
        </Box>
        <Typography variant="h5" fontWeight={600} gutterBottom>
          {t("onboarding.completion.title")}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {t("onboarding.completion.message")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t("onboarding.completion.helpText")}{" "}
          <MuiLink href="mailto:support@ruumi.io" underline="hover">
            {t("onboarding.completion.helpLink")}
          </MuiLink>
        </Typography>
        <Button variant="contained" size="large" onClick={onClose}>
          {t("onboarding.completion.button")}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

// ============ COLLAPSED FAB ============

/**
 * Floating action button shown when checklist is minimized.
 * Click to expand the checklist again.
 */
export function OnboardingFab() {
  const { t } = useTranslation();
  const {
    isComplete,
    isDismissed,
    completedSteps,
    totalSteps,
    isLoading,
    resetDismissal,
  } = useOnboardingState();

  if (isComplete || isLoading) {
    return null;
  }

  // If dismissed, show a small FAB to re-open
  if (isDismissed) {
    return (
      <Fab
        size="small"
        color="primary"
        onClick={resetDismissal}
        sx={{
          position: "fixed",
          top: 80,
          right: 24,
          zIndex: 1400,
        }}
        aria-label={t("onboarding.checklist.show")}
      >
        <Typography variant="caption" fontWeight={600}>
          {completedSteps}/{totalSteps}
        </Typography>
      </Fab>
    );
  }

  return null;
}

// ============ STYLED COMPONENTS ============

const FloatingContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isMobile",
})<{ isMobile: boolean }>(({ isMobile }) => ({
  position: "fixed",
  top: 16,
  right: 16,
  zIndex: 1400,
  width: isMobile ? "calc(100% - 32px)" : 320,
  maxWidth: "100%",
}));
